export const PAYMENT_API_PATHS = {
  razorpayCheckout: '/payments/razorpay/checkout',
  razorpayVerify: '/payments/razorpay/verify',
} as const

export const PAYMENT_GATEWAY = {
  razorpay: 'razorpay',
  cashfree: 'cashfree',
} as const

export type PaymentGatewayId = (typeof PAYMENT_GATEWAY)[keyof typeof PAYMENT_GATEWAY]

export const PAYMENT_UI = {
  cashfreeUnavailable: 'Online payments via Cashfree are not available yet.',
  gatewayRequired: 'Online payment is not available for this order yet.',
} as const

export const CHECKOUT_SETTLE = {
  paid: 'paid',
  dismissed: 'dismissed',
  unverified: 'unverified',
  expired: 'expired',
} as const

export type CheckoutSettleStatus = (typeof CHECKOUT_SETTLE)[keyof typeof CHECKOUT_SETTLE]

export const RAZORPAY_CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js'
export const RAZORPAY_SCRIPT_ID = 'razorpay-checkout'

export type RazorpayPrefill = {
  name: string
  email: string
  contact: string
}

export type RazorpayCheckoutPayload = {
  keyId: string
  orderId: string
  amount: number
  currency: string
  name: string
  description: string
  prefill: RazorpayPrefill
}

export type RazorpayPaymentSuccess = {
  razorpay_payment_id: string
  razorpay_order_id: string
  razorpay_signature: string
}

export type RazorpayCheckoutResult =
  | { status: 'paid'; payment: RazorpayPaymentSuccess }
  | { status: 'dismissed' }
  | { status: 'expired' }

export type PaymentCheckoutSession = {
  gateway: PaymentGatewayId
  razorpay: RazorpayCheckoutPayload | null
}

export type PaidGatewayPayment =
  | {
      status: 'paid'
      gateway: typeof PAYMENT_GATEWAY.razorpay
      payment: RazorpayPaymentSuccess
    }

export type PaymentCollectResult =
  | PaidGatewayPayment
  | { status: 'dismissed' }
  | { status: 'expired' }

export type PaymentCollectOptions = {
  timeoutMs?: number
}

export type PaymentGatewayAdapter = {
  gateway: PaymentGatewayId
  start: (checkoutId: string) => Promise<PaymentCheckoutSession>
  collect: (
    session: PaymentCheckoutSession,
    options?: PaymentCollectOptions,
  ) => Promise<PaymentCollectResult>
  verify: (checkoutId: string, collected: PaidGatewayPayment) => Promise<unknown>
}

export type VerifyRazorpayInput = {
  checkoutId: string
  razorpayOrderId: string
  razorpayPaymentId: string
  razorpaySignature: string
}

export type StartRazorpayCheckoutInput = {
  checkoutId: string
}

export type RazorpayBrowserOptions = {
  key: string
  amount: number
  currency: string
  name: string
  description: string
  order_id: string
  prefill: RazorpayPrefill
  handler: (response: RazorpayPaymentSuccess) => void
  modal?: { ondismiss?: () => void }
}

export type RazorpayCheckoutInstance = {
  open: () => void
  close: () => void
}

export type RazorpayConstructor = new (
  options: RazorpayBrowserOptions,
) => RazorpayCheckoutInstance
